/**
 * Weather over the garden, and the mutations it leaves behind.
 *
 * ## The constraint that shaped this
 *
 * Growth is derived from the wall clock and never accumulated per frame —
 * that is what makes offline progress a subtraction rather than a simulation
 * (see `docs/ARCHITECTURE.md`). Weather that changed the growth *rate* would
 * normally destroy that: you cannot integrate a rate you did not observe.
 *
 * So weather is not rolled and stored. It is a **pure function of time**.
 * The clock is cut into fixed windows, each window's weather is derived from
 * a hash of its index, and the effective grow time over any interval is a
 * closed-form sum over the windows it spans. `growMsBetween(a, b)` is exactly
 * as correct for eight hours the player slept through as for the last frame,
 * and needs no record of either.
 *
 * The same property gives mutations for free: a matured specimen's mutation
 * is recomputed from the window it grew in, so it survives a reload without
 * being serialised and can never disagree with itself.
 *
 * ## Engine and content
 *
 * This file is the timeline. The skies themselves — names, rates, colours,
 * marks, draw weights and which one is pinned to the clock — are this game's
 * content, in `src/content/weather.ts`.
 */

import {
  CLOCK_WEATHER,
  DEFAULT_WEATHER,
  MUTATIONS,
  WEATHER,
  WEATHER_DRAW,
  type Mutation,
  type MutationId,
  type Weather,
} from '../content/weather';

/**
 * Length of one weather window.
 *
 * Four minutes is a compromise found by trying others. Ten made weather
 * invisible to the 45-second acacia, which is the only species a one-visit
 * player ever sees — losing the entire feature for exactly the audience that
 * opens the link once. Under two, weather flickered too fast to
 * read on the HUD before it changed again.
 */
export const WINDOW_MS = 4 * 60 * 1000;

/* ------------------------------------------------------------------ *
 * The timeline
 * ------------------------------------------------------------------ */

/** Integer hash — the whole timeline hangs off this being stable forever. */
function hash32(n: number): number {
  let x = n | 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  return (x ^ (x >>> 16)) >>> 0;
}

export function windowIndex(ms: number): number {
  return Math.floor(ms / WINDOW_MS);
}

export function windowStart(index: number): number {
  return index * WINDOW_MS;
}

/** The weather in a given window. Pure, stable, and the same for everyone. */
export function weatherForWindow(index: number): Weather {
  // Golden hour is read off the real clock, so it lands at the player's own
  // sunrise and sunset rather than at an arbitrary offset from epoch.
  const hour = new Date(windowStart(index) + WINDOW_MS / 2).getHours();
  if (CLOCK_WEATHER.hours.includes(hour)) return WEATHER[CLOCK_WEATHER.id];

  const roll = hash32(index * 2654435761) / 4294967296;
  let acc = 0;
  for (const entry of WEATHER_DRAW) {
    acc += entry.weight;
    if (roll < acc) return WEATHER[entry.id];
  }
  return WEATHER[DEFAULT_WEATHER];
}

export function weatherAt(ms: number): Weather {
  return weatherForWindow(windowIndex(ms));
}

/** Milliseconds until the sky changes. Drives nothing; it is HUD garnish. */
export function msUntilWeatherChange(ms: number): number {
  return windowStart(windowIndex(ms) + 1) - ms;
}

/* ------------------------------------------------------------------ *
 * Integrating growth across the timeline
 * ------------------------------------------------------------------ */

/**
 * Effective grow time accrued between two moments.
 *
 * `capMs` is not an optimisation detail, it is what keeps this bounded: a
 * caller passes the amount still needed for maturity, and the sum stops as
 * soon as it is reached. Without it, a plant sown a month ago would walk ten
 * thousand windows on every call.
 */
export function growMsBetween(fromMs: number, toMs: number, capMs = Infinity): number {
  if (toMs <= fromMs || capMs <= 0) return 0;

  let total = 0;
  let cursor = fromMs;
  let index = windowIndex(fromMs);
  const last = windowIndex(toMs);

  while (index <= last) {
    const end = Math.min(toMs, windowStart(index + 1));
    total += (end - cursor) * weatherForWindow(index).rate;
    if (total >= capMs) return total;
    cursor = end;
    index += 1;
  }
  return total;
}

/** Bounded at roughly five and a half days of windows. */
const MAX_SOLVE_WINDOWS = 2000;

/**
 * The moment at which `neededMs` of effective grow time will have accrued,
 * starting from `fromMs`. The inverse of `growMsBetween`, and the only way to
 * answer "next tree in 4m" when the rate is not constant.
 */
export function solveGrowTime(fromMs: number, neededMs: number): number {
  if (neededMs <= 0) return fromMs;

  let remaining = neededMs;
  let cursor = fromMs;
  let index = windowIndex(fromMs);

  for (let i = 0; i < MAX_SOLVE_WINDOWS; i += 1) {
    const rate = weatherForWindow(index).rate;
    const end = windowStart(index + 1);
    const available = (end - cursor) * rate;
    if (available >= remaining) return cursor + remaining / rate;
    remaining -= available;
    cursor = end;
    index += 1;
  }
  return cursor;
}

/* ------------------------------------------------------------------ *
 * Mutations
 * ------------------------------------------------------------------ */

/**
 * A specimen has to have spent this much of its life under one weather to
 * carry its mark. Below it, nothing — most trees should be ordinary, or a
 * mutation is not a prize.
 *
 * Note what this means for the fast species. A 45-second acacia lives inside
 * a single four-minute window, so it simply takes the sky it was sown under
 * and is marked about half the time. That is deliberate: it hands the
 * one-visit player the whole mechanic and the fact card behind it, and it
 * makes sowing *timeable* — watch the chip, wait for the storm, plant into
 * it. A sidr, living forty-five minutes across a dozen windows, needs real
 * sustained exposure and stays ordinary about three times in four.
 */
const MUTATION_THRESHOLD = 0.3;

/**
 * Which mutation a plant grown between two moments carries, if any.
 *
 * Shares are of wall-clock time rather than of effective grow time: "it spent
 * a third of its life in a sandstorm" is the claim a player can check against
 * what they watched, and rate-weighting it would quietly favour rain.
 */
export function mutationBetween(fromMs: number, toMs: number): Mutation | null {
  if (toMs <= fromMs) return null;

  const span = toMs - fromMs;
  const shares = new Map<MutationId, number>();

  let cursor = fromMs;
  let index = windowIndex(fromMs);
  const last = windowIndex(toMs);

  for (let i = 0; index <= last && i < MAX_SOLVE_WINDOWS; i += 1) {
    const end = Math.min(toMs, windowStart(index + 1));
    const weather = weatherForWindow(index);
    if (weather.mutation) {
      shares.set(weather.mutation, (shares.get(weather.mutation) ?? 0) + (end - cursor));
    }
    cursor = end;
    index += 1;
  }

  let best: MutationId | null = null;
  let bestShare = 0;
  for (const [id, ms] of shares) {
    if (ms > bestShare) {
      bestShare = ms;
      best = id;
    }
  }

  if (!best || bestShare / span < MUTATION_THRESHOLD) return null;
  return MUTATIONS[best];
}
